import React, { useState } from 'react';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { Check, North, South, Sort as SortIcon } from '@mui/icons-material';

// One flat list of concrete (field, order) choices rather than a field-picker
// next to a separate direction-picker — "Budget (High → Low)" is one click,
// not two, and each label already reads naturally without a direction icon
// needing to be interpreted on its own.
export const SORT_OPTIONS = [
    { key: 'name-asc', field: 'name', order: 'asc', label: 'Name (A → Z)' },
    { key: 'name-desc', field: 'name', order: 'desc', label: 'Name (Z → A)' },
    { key: 'budget-desc', field: 'budget', order: 'desc', label: 'Budget (High → Low)' },
    { key: 'budget-asc', field: 'budget', order: 'asc', label: 'Budget (Low → High)' },
    { key: 'limit-desc', field: 'limit', order: 'desc', label: 'Limit (High → Low)' },
    { key: 'limit-asc', field: 'limit', order: 'asc', label: 'Limit (Low → High)' },
    { key: 'revenue-desc', field: 'revenue', order: 'desc', label: 'Revenue (High → Low)' },
    { key: 'revenue-asc', field: 'revenue', order: 'asc', label: 'Revenue (Low → High)' },
];

export const DEFAULT_SORT = { field: 'default', order: null };

const OPTION_BY_KEY = Object.fromEntries(SORT_OPTIONS.map((o) => [o.key, o]));

export function sortKeyOf(sort) {
    return sort.field === 'default' ? null : `${sort.field}-${sort.order}`;
}

// Revenue is a live-computed, super-admin-only figure that only exists for
// whichever month the Revenue filter has actually loaded (see Home.jsx) —
// unlike Name/Budget/Limit, which are plain fields on every campaign
// regardless of view. Rather than hiding the option outright (which would
// make it look like sorting by revenue doesn't exist at all), it's shown
// disabled with an explanation, discoverable but clearly gated.
const CampaignSort = React.memo(function CampaignSort({ sort, onChange, revenueAvailable }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const activeKey = sortKeyOf(sort);
    const activeOption = activeKey ? OPTION_BY_KEY[activeKey] : null;

    const handleSelect = (option) => {
        onChange({ field: option.field, order: option.order });
        setAnchorEl(null);
    };

    const handleReset = () => {
        onChange(DEFAULT_SORT);
        setAnchorEl(null);
    };

    return (
        <>
            <Button
                variant={activeOption ? 'contained' : 'outlined'}
                size="small"
                startIcon={<SortIcon fontSize="small" />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {activeOption ? activeOption.label : 'Sort'}
            </Button>

            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {SORT_OPTIONS.map((option) => {
                    const disabled = option.field === 'revenue' && !revenueAvailable;
                    const item = (
                        <MenuItem
                            key={option.key}
                            selected={option.key === activeKey}
                            disabled={disabled}
                            onClick={() => handleSelect(option)}
                        >
                            <ListItemIcon>
                                {option.key === activeKey ? (
                                    <Check fontSize="small" />
                                ) : option.order === 'asc' ? (
                                    <North fontSize="small" sx={{ opacity: 0.5 }} />
                                ) : (
                                    <South fontSize="small" sx={{ opacity: 0.5 }} />
                                )}
                            </ListItemIcon>
                            <ListItemText>{option.label}</ListItemText>
                        </MenuItem>
                    );
                    // A disabled MenuItem still blocks its own Tooltip from showing
                    // (no pointer events to trigger it) — the span wrapper keeps the
                    // hover target alive so the "why" is actually discoverable.
                    return disabled ? (
                        <Tooltip key={option.key} title="Select a month under Revenue to sort by this" placement="right">
                            <span>{item}</span>
                        </Tooltip>
                    ) : (
                        item
                    );
                })}
                <MenuItem disabled={!activeOption} onClick={handleReset}>
                    <ListItemText sx={{ color: 'text.secondary' }}>Clear sort (newest first)</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
});

export default CampaignSort;
